import { fetchAndActivate } from 'firebase/remote-config'

import { F } from '../context'
import { chatsLimits, GetVariable, scrolls, setInputParameter, setLocalVariables, Update } from '../hooks'
import {
  BarComponentType,
  BrandingThemeType,
  ComponentType,
  Config,
  createLogo,
  createScreen,
  createScreenCorners,
  createTabBar,
  createTopBar,
  getInputParameters,
  NEED_TOKEN,
  runActions,
  Screen,
  ScreenSize,
  setComponents,
  setTabs,
  startLoader,
  stopLoader,
  toast,
} from './index'
import { postCompaniesForParentWindow } from './postCompaniesForParentWindow'

export const getScreen = async (
  f: F,
  currentScreen: Screen,
  config: Config,
  parentElement: HTMLElement,
  desktopMode: boolean,
  theme: BrandingThemeType,
  launchOpen: boolean,
  setLaunchOpen: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: (pathname: string) => void,
  navigateBack: () => void,
  openDialog: (page: string, beforeClose?: () => Promise<void>) => void,
  closeDialog: () => Promise<void>,
  closeAllDialogs: () => void,
  getVariable: GetVariable,
  update: Update,
  language: string,
  email?: string,
  token?: string,
  leading?: boolean,
  trailing?: boolean,
  inputParameterValue?: string,
  searchParams?: URLSearchParams
) => {
  startLoader()
  window.onscroll = null
  const { auth, remoteConfig, emailKey } = f
  const screenConfig = JSON.parse(JSON.stringify(currentScreen)) as Screen
  const {
    id,
    screenName,
    size,
    components = [],
    topBar,
    showTopBar,
    showBackButton,
    screenTitle,
    onScreenVisitActions,
    inputParameters,
    localVariables,
    dialog,
  } = screenConfig
  const key = inputParameterValue ? `${screenName}/${inputParameterValue}` : screenName
  const {
    data: { collections = [] },
    screens = [],
    branding: {
      typography: { iosDefaultFontFamily: fontFamily, fontStyles = [] },
    },
    tabBars = [],
  } = config
  const tabBar = tabBars.find(el => el.tabs?.find(el => el.screen === screenName))
  const screenId = `screen.${screenName}.${id}`
  const tabBarId = `${BarComponentType.tabBar}.${tabBar?.name}.${tabBar?.id}`
  const topBarId = `${BarComponentType.topBar}.top bar.${id}-${BarComponentType.topBar}`
  const getVariableValue = getVariable(screenConfig)
  const runOnScreenVisitActions = () =>
    onScreenVisitActions &&
    runActions(
      f,
      id,
      onScreenVisitActions,
      navigate,
      navigateBack,
      openDialog,
      closeDialog,
      closeAllDialogs,
      screens,
      screenConfig,
      getVariableValue,
      getVariable,
      update
    )
  if (inputParameters) {
    const { inputParameter, searchInputParameters } = getInputParameters(inputParameters)
    for (const { parameter } of inputParameters) {
      const screenInputParameter = inputParameter?.parameter === parameter ? inputParameter : undefined
      const screenSearchInputParameter = searchInputParameters?.find(el => el.parameter === parameter)
      if (screenInputParameter && inputParameterValue) {
        await setInputParameter(id, getVariableValue, screenInputParameter, inputParameterValue)
      }
      if (screenSearchInputParameter && searchParams) {
        const value = searchParams.get(screenSearchInputParameter.parameter)
        await setInputParameter(id, getVariableValue, screenSearchInputParameter, value)
      }
    }
  }
  if (localVariables) {
    await setLocalVariables(id, getVariableValue, localVariables)
  }
  if (!launchOpen) {
    const cancelled = await runOnScreenVisitActions()
    if (cancelled) {
      return
    }
  }
  if (!trailing) {
    parentElement.innerHTML = ''
  }
  const screen = await createScreen(
    screenId,
    screenConfig,
    parentElement,
    getVariableValue,
    size,
    !!tabBar,
    leading,
    trailing
  )
  const desktopRoot = desktopMode && size === ScreenSize.responsive && !leading && !trailing && components.length === 1
  const desktopRootScroll = desktopRoot && components[0].componentType === ComponentType.scroll
  const desktopRootList = desktopRoot && components[0].componentType === ComponentType.list
  if (desktopRootScroll || desktopRootList) {
    parentElement.style.removeProperty('height')
    screen.style.removeProperty('height')
  } else {
    parentElement.style.height = '100%'
  }
  if (desktopMode) {
    createScreenCorners(screen)
    if (!trailing && !dialog) {
      await createLogo(
        screen,
        fontFamily,
        size === ScreenSize.compact && !tabBar,
        getVariableValue,
        email || token
          ? undefined
          : () => {
              setLaunchOpen(true)
              navigate('/')
            }
      )
    }
  }
  const showTopBarValue = await getVariable(screenConfig)({
    ...showTopBar,
    booleanConstant: showTopBar?.constant,
  })
  if (showTopBarValue) {
    await createTopBar(
      f,
      topBarId,
      screen,
      fontFamily,
      fontStyles,
      theme,
      desktopMode,
      navigate,
      navigateBack,
      openDialog,
      closeDialog,
      closeAllDialogs,
      screens,
      screenConfig,
      getVariableValue,
      getVariable,
      update,
      language,
      topBar,
      showBackButton,
      screenTitle
    )
  }
  if (tabBar) {
    const screenTabBar = await createTabBar(tabBarId, tabBar, screen, getVariableValue)
    await setTabs(screenName, tabBar, screenTabBar, fontFamily, desktopMode, navigate, getVariableValue)
  }
  if (components.length) {
    await setComponents(
      f,
      components,
      screen,
      fontFamily,
      fontStyles,
      theme,
      desktopRootScroll,
      desktopRootList,
      true,
      desktopMode,
      navigate,
      navigateBack,
      openDialog,
      closeDialog,
      closeAllDialogs,
      screens,
      screenConfig,
      getVariableValue,
      getVariable,
      update,
      language,
      func => (scrolls[key] = func),
      chatsLimits[key] || 30,
      limit => (chatsLimits[key] = limit)
    )
    scrolls[key]?.()
    if (desktopMode && !scrolls[key] && !dialog) {
      window.scrollTo({ top: 0 })
    }
  }
  stopLoader()
  if (launchOpen) {
    try {
      await fetchAndActivate(remoteConfig)
      await new Promise((resolve, reject) => {
        const unsubscribe = auth.onAuthStateChanged(
          async () => {
            unsubscribe()
            resolve(null)
          },
          err => {
            unsubscribe()
            reject(err)
          }
        )
      })
      if (email) {
        if (email === auth.currentUser?.email) {
          postCompaniesForParentWindow(f, collections, getVariableValue)
        } else {
          window.parent.postMessage(NEED_TOKEN, '*')
          return
        }
      } else if (token) {
        await auth.signInWithCustomToken(token)
        postCompaniesForParentWindow(f, collections, getVariableValue)
      } else if (auth.isSignInWithEmailLink(window.location.href)) {
        const email = localStorage.getItem(emailKey)
        if (email) {
          await auth.signInWithEmailLink(email, window.location.href)
          localStorage.removeItem(emailKey)
        }
      }
    } catch (err: any) {
      toast(err)
    }
    await runOnScreenVisitActions()
    setLaunchOpen(false)
  }
}
